/**
 * Poll and refresh token hook.
 * This file exists to break circular dependencies between preauth-company.slice.ts and brightback-auth.slice.ts
 */

import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { LogInPaths } from '@bbkAdminComponents/log-in-flow/log-in-paths';
import { isUnauthorizedError } from '@bbkAdminRedux/rtkq/auth-status';
import { selectAuthStatus } from '@bbkAdminRedux/rtkq/auth-utils';
import {
  brightbackAuthSlice,
  CONFIG_NOT_LOADED_ERROR,
  NOT_SIGNED_IN_ERROR,
  UNSUPPORTED_AUTH_SESSION_ERROR,
} from '@bbkAdminRedux/rtkq/brightback-auth.slice';
import BrightbackSession from '@bbkAdminUtils/brightback-session';
import { GlobalLogger } from '@bbkAdminUtils/GlobalLogger';

// Refresh this many seconds before the access token is due to expire.
const REFRESH_LEAD_SECONDS = 30;
// Floor for the poll interval once the token is at/near expiry.
const MIN_POLL_SECONDS = 10;
// Ceiling for exponential backoff after transient refresh failures.
const MAX_BACKOFF_SECONDS = 300;

const getTimeRemaining = () =>
  (BrightbackSession.getAccessTokenExpiresIn() ?? 0) -
  BrightbackSession.getAccessTokenAgeInSeconds();

const getErrorMessage = (err: unknown) =>
  err instanceof Error
    ? err.message
    : typeof err === 'object' &&
        err !== null &&
        'message' in err &&
        typeof err.message === 'string'
      ? err.message
      : undefined;

const getErrorStatus = (err: unknown): number | undefined => {
  if (!err || typeof err !== 'object') {
    return undefined;
  }
  const status = (err as { status?: unknown }).status;
  return typeof status === 'number' ? status : undefined;
};

/**
 * A failed refresh is terminal (the session is genuinely unusable, so sign out)
 * only for: an unsupported/legacy stored session, a missing refresh token, or a
 * server rejection (401/403). Everything else — a rate-limited 429, a 5xx, or a
 * network/timeout error — is transient: the previous behavior signed the user
 * out on any failure, so a momentary blip during the ~15-minute refresh logged
 * them out. Transient failures are retried with backoff instead; a truly dead
 * session still surfaces as a 401 on the next real request and is handled by the
 * unauthorized-redirect middleware.
 */
const isTerminalRefreshError = (err: unknown): boolean => {
  const message = getErrorMessage(err);
  if (
    message === UNSUPPORTED_AUTH_SESSION_ERROR ||
    message === NOT_SIGNED_IN_ERROR
  ) {
    return true;
  }
  if (isUnauthorizedError(err)) {
    return true;
  }
  return getErrorStatus(err) === 403;
};

export const usePollAndRefreshToken = () => {
  const [doRefreshTokens$] =
    brightbackAuthSlice.endpoints.doRefreshTokens.useMutation();
  const [signOut$] = brightbackAuthSlice.endpoints.signOut.useMutation();

  // Mirror the reactive auth status into a ref so the self-scheduling timer
  // (memoized with []) reads the latest value at fire time without an
  // imperative store read (window.bbkStore).
  const authStatus = useSelector(selectAuthStatus);
  const authStatusRef = useRef(authStatus);
  authStatusRef.current = authStatus;

  // Consecutive transient refresh failures, driving exponential backoff so a
  // momentary 429/5xx does not immediately log the user out.
  const transientFailuresRef = useRef(0);

  const normalDelay = useCallback(() => {
    const remaining = getTimeRemaining();
    return remaining > 60 ? remaining - REFRESH_LEAD_SECONDS : MIN_POLL_SECONDS;
  }, []);

  // Runs one refresh attempt when appropriate and returns how many seconds to
  // wait before the next tick, or `null` to stop polling (dev sessions).
  const runRefreshCycle = useCallback(async (): Promise<number | null> => {
    if (authStatusRef.current !== 'authenticated') {
      return normalDelay();
    }
    // Dev sessions do not support (and do not need) token refresh.
    if (BrightbackSession.getAccessTokenType() === 'dev') {
      return null;
    }

    try {
      await doRefreshTokens$().unwrap();
      transientFailuresRef.current = 0;
      return normalDelay();
    } catch (err) {
      const message = getErrorMessage(err);
      // The client config never loaded (e.g. a transient /client_config failure
      // on boot, which AdminApp already logs). Both refresh and sign-out depend
      // on it, so there is nothing actionable here: retry on the next tick.
      if (message === CONFIG_NOT_LOADED_ERROR) {
        return MIN_POLL_SECONDS;
      }

      if (isTerminalRefreshError(err)) {
        transientFailuresRef.current = 0;
        if (message !== UNSUPPORTED_AUTH_SESSION_ERROR) {
          GlobalLogger.error(err);
        }
        await signOut$({ redirect: LogInPaths.signIn })
          .unwrap()
          .catch(() => {
            // signOut navigates away on success; swallow its own failure so a
            // rejected sign-out does not surface as an unhandled rejection.
          });
        return MAX_BACKOFF_SECONDS;
      }

      // Transient failure: keep the session and retry with exponential backoff.
      transientFailuresRef.current += 1;
      GlobalLogger.error(err);
      return Math.min(
        MIN_POLL_SECONDS * 2 ** (transientFailuresRef.current - 1),
        MAX_BACKOFF_SECONDS
      );
    }
  }, [doRefreshTokens$, normalDelay, signOut$]);

  const scheduleNextRefresh = useCallback(
    (delaySeconds: number) => {
      if (delaySeconds > MIN_POLL_SECONDS) {
        console.log(
          `usePollAndRefreshToken, set up timer to refresh token in ${delaySeconds} seconds`
        );
      }
      setTimeout(async () => {
        const nextDelaySeconds = await runRefreshCycle();
        if (nextDelaySeconds !== null) {
          scheduleNextRefresh(nextDelaySeconds);
        }
      }, delaySeconds * 1000);
    },
    [runRefreshCycle]
  );

  useEffect(() => {
    scheduleNextRefresh(MIN_POLL_SECONDS);
  }, [scheduleNextRefresh]);
};
